// /src/components/SSBCounterpartyConcentrationReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");
const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(Number(n));

const int0 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : new Intl.NumberFormat("en-US").format(Number(n));

const pct2 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(2)}%`;

const shortWeek = (w) => (w || "").slice(0, 10);

const normalizeName = (s) => {
  const v = (s || "").trim();
  if (!v) return "UNMAPPED COUNTERPARTY";
  return v.replace(/\s+/g, " ");
};

const kpiCard = (label, value, sub) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E7ECF3",
      borderRadius: 14,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 86,
    }}
  >
    <div style={{ fontSize: 12, color: "#5E6B7A", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
    {sub ? <div style={{ fontSize: 12, color: "#7A8696", marginTop: 6 }}>{sub}</div> : null}
  </div>
);

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.2,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      {text}
    </span>
  );
};

const metricLabel = (m) => (m === "count" ? "Txn Count" : "Volume ($)");

function TooltipBar({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #E7ECF3",
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 16px 40px rgba(16,24,40,0.12)",
        maxWidth: 360,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 6, color: "#0B1F33" }}>{p.fullName}</div>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
        {p.country ? `Country: ${p.country}` : "Country: —"}
      </div>
      <div style={{ fontSize: 13, color: "#0B1F33" }}>
        <div>Txn Count: <b>{int0(p.txn_count)}</b></div>
        <div>Total Amount: <b>{money0(p.total_amount)}</b></div>
        <div>Share: <b>{pct2(p.share_pct)}</b></div>
      </div>
    </div>
  );
}

// Build period options from available weeks
function buildPeriodMeta(weeks, periodType) {
  if (periodType === "week") {
    return weeks.map((w) => ({ label: shortWeek(w), weeks: [w] }));
  }
  const map = new Map();
  for (const w of weeks) {
    let key;
    if (periodType === "month") {
      key = w.slice(0, 7); // "2026-04"
    } else {
      const year = w.slice(0, 4);
      const q = Math.ceil(parseInt(w.slice(5, 7)) / 3);
      key = `${year}-Q${q}`;
    }
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(w);
  }
  return Array.from(map.entries()).map(([label, ws]) => ({ label, weeks: ws }));
}

const PERIOD_TYPES = ["week", "month", "quarter"];

// "" sentinel = "All Programs" (omits program_name param from API call)
const ALL_PROGRAMS = "";

export default function SSBCounterpartyConcentrationReport() {
  const [programs, setPrograms] = useState([]);
  const [weeks, setWeeks] = useState([]);

  const [programName, setProgramName] = useState(ALL_PROGRAMS);
  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [metric, setMetric] = useState("amount");
  const [limit, setLimit] = useState(25);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [err, setErr] = useState("");

  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]);

  // Available period options derived from weeks list + current period type
  const periodMeta = useMemo(() => buildPeriodMeta(weeks, periodType), [weeks, periodType]);

  // Load filters once on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingFilters(true);
      setErr("");
      try {
        const resp = await fetch(`${API_BASE}/v1/ssb/filters/counterparty-concentration`);
        const json = await resp.json();
        if (!mounted) return;
        setPrograms(Array.isArray(json.programs) ? json.programs : []);
        setWeeks(Array.isArray(json.weeks) ? json.weeks : []);
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load filters. Is the API running?");
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // When periodMeta changes (weeks loaded or periodType changed), select the first option
  useEffect(() => {
    if (periodMeta.length > 0) {
      setSelectedPeriod((prev) => {
        const stillValid = periodMeta.some((m) => m.label === prev);
        return stillValid ? prev : periodMeta[0].label;
      });
    }
  }, [periodMeta]);

  // Load report whenever any filter changes
  useEffect(() => {
    if (!selectedPeriod || periodMeta.length === 0) return;

    const meta = periodMeta.find((m) => m.label === selectedPeriod);
    if (!meta) return;

    let mounted = true;

    (async () => {
      setLoadingReport(true);
      setErr("");
      try {
        // meta.weeks covers only the selected period (1 week for "week" view,
        // 4-5 for "month", 13 for "quarter") — the backend aggregates the whole
        // range server-side, so this is a single call regardless of period type.
        const orderedWeeks = meta.weeks.slice().sort();
        const rangeStart = orderedWeeks[0];
        const rangeEnd = orderedWeeks[orderedWeeks.length - 1];

        const url = new URL(`${API_BASE}/v1/ssb/counterparties/concentration`);
        if (programName) url.searchParams.set("program_name", programName);
        url.searchParams.set("week_start", rangeStart);
        if (rangeEnd !== rangeStart) url.searchParams.set("week_end", rangeEnd);
        url.searchParams.set("limit", String(limit));
        url.searchParams.set("order_by", metric);

        const resp = await fetch(url.toString());
        if (!mounted) return;

        if (!resp.ok) { setReport(null); return; }
        const combined = await resp.json();
        if (!mounted) return;

        setReport(combined);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load report: ${e.message || e}`);
        if (mounted) setReport(null);
      } finally {
        if (mounted) setLoadingReport(false);
      }
    })();

    return () => { mounted = false; };
  }, [programName, selectedPeriod, periodMeta, limit, metric]);

  // Trend: always last 12 weekly data points regardless of period type
  useEffect(() => {
    if (weeks.length === 0) { setTrend([]); return; }

    const controller = new AbortController();
    (async () => {
      try {
        const lastN = weeks.slice(0, 12);
        const fetched = await Promise.all(
          lastN.map(async (wk) => {
            const url = new URL(`${API_BASE}/v1/ssb/counterparties/concentration`);
            if (programName) url.searchParams.set("program_name", programName);
            url.searchParams.set("week_start", wk);
            url.searchParams.set("limit", "5");
            url.searchParams.set("order_by", metric === "count" ? "count" : "amount");
            const resp = await fetch(url.toString(), { signal: controller.signal });
            if (!resp.ok) return null;
            const json = await resp.json();
            const rws = Array.isArray(json?.rows) ? json.rows : [];
            if (metric === "count") {
              const total = Number(json?.summary?.txn_count || 0);
              const top1Cnt = rws.length > 0 ? Number(rws[0].txn_count || 0) : 0;
              const top1Share = total > 0 ? (top1Cnt / total) * 100 : 0;
              const top5Cnt = rws.reduce((s, r) => s + Number(r.txn_count || 0), 0);
              const top5Share = total > 0 ? (top5Cnt / total) * 100 : 0;
              return { week: shortWeek(wk), top1_share_pct: top1Share, top5_share_pct: top5Share };
            } else {
              const total = Number(json?.summary?.total_amount || 0);
              const top1 = Number(json?.summary?.top1_share_pct || 0);
              const top5Amt = rws.reduce((s, r) => s + Number(r.total_amount || 0), 0);
              const top5Share = total > 0 ? (top5Amt / total) * 100 : 0;
              return { week: shortWeek(wk), top1_share_pct: top1, top5_share_pct: top5Share };
            }
          })
        );
        const results = fetched.filter(Boolean).sort((a, b) => (a.week > b.week ? 1 : -1));
        setTrend(results);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Trend load failed:", e);
      }
    })();

    return () => { controller.abort(); };
  }, [programName, weeks, metric]);

  const rows = report?.rows || [];
  const summary = report?.summary || {};

  const chartData = useMemo(() => {
    return rows.map((r) => {
      const fullName = normalizeName(r.counterparty_name);
      const name = fullName.length > 28 ? fullName.slice(0, 28) + "…" : fullName;
      return {
        name,
        fullName,
        country: r.country || "—",
        txn_count: Number(r.txn_count || 0),
        total_amount: Number(r.total_amount || 0),
        share_pct: Number(r.share_pct || 0),
        value: metric === "count" ? Number(r.txn_count || 0) : Number(r.total_amount || 0),
      };
    });
  }, [rows, metric]);

  const top5Share = useMemo(() => {
    const total = Number(summary.total_amount || 0);
    if (!total || rows.length === 0) return 0;
    return (rows.slice(0, 5).reduce((s, r) => s + Number(r.total_amount || 0), 0) / total) * 100;
  }, [rows, summary.total_amount]);

  const top1Share = Number(summary.top1_share_pct || 0);
  const alertTone = top5Share >= 60 ? "bad" : top5Share >= 45 ? "warn" : "good";
  const periodLabel = { week: "Week", month: "Month", quarter: "Quarter" }[periodType];
  const bannerText =
    rows.length === 0
      ? `No data for selected ${periodLabel.toLowerCase()}.`
      : `Top 5 counterparties represent ${top5Share.toFixed(1)}% of ${periodLabel.toLowerCase()} volume`;
  const bannerSub =
    rows.length === 0
      ? ""
      : `Top 1 exposure: ${top1Share.toFixed(1)}% • Distinct counterparties: ${int0(summary.distinct_counterparties)} • Total volume: ${money0(summary.total_amount)}`;

  const toggleStyle = (active) => ({
    padding: "3px 9px",
    borderRadius: 6,
    border: `1px solid ${active ? "#3730A3" : "#D7DEE9"}`,
    background: active ? "#EEF2FF" : "#fff",
    color: active ? "#3730A3" : "#64748B",
    fontSize: 11,
    fontWeight: active ? 800 : 500,
    cursor: "pointer",
    lineHeight: 1.4,
  });

  return (
    <SSBReportShell
      title="SSB Dashboard — Counterparty Concentration Monitoring"
      subtitle={`Program: ${programName || "All Programs"} • ${periodLabel}: ${selectedPeriod || "—"}`}
      rightMeta={[
        `Metric: ${metricLabel(metric)}`,
        `Top N: ${limit}`,
        loadingReport ? "Loading…" : "Live",
      ]}
      leftMeta={[
        "Sponsor bank oversight view",
        "Counterparty concentration & exposure monitoring",
      ]}
      footerLeft="Idenza • SSB Reporting (Phase 1)"
      footerRight="Source: mv_ssb_reports_counterparty_concentration_weekly"
    >
      {/* Filters */}
      <SSBFiltersBar>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Program</div>
          <select
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            disabled={loadingFilters}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Period</div>
            <div style={{ display: "flex", gap: 3 }}>
              {PERIOD_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setPeriodType(t)}
                  style={toggleStyle(periodType === t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={loadingFilters || periodMeta.length === 0}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {periodMeta.map((m) => (
              <option key={m.label} value={m.label}>{m.label}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Metric</div>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="amount">Volume ($)</option>
            <option value="count">Txn Count</option>
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Top N</div>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </SSBFiltersBar>

      {/* Banner */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Pill text="Concentration Signal" tone={alertTone} />
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0B1F33" }}>{bannerText}</div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>{bannerSub}</div>
        </div>
        <div>{loadingReport ? <Pill text="Loading…" tone="neutral" /> : <Pill text="Live" tone="good" />}</div>
      </div>

      {/* Error */}
      {err ? (
        <div style={{ background: "#FEF3F2", border: "1px solid #FECACA", color: "#7F1D1D", padding: 12, borderRadius: 16, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        {kpiCard(`Total ${periodLabel} Volume`, money0(summary.total_amount))}
        {kpiCard("Total Transactions", int0(summary.txn_count))}
        {kpiCard("Distinct Counterparties", int0(summary.distinct_counterparties))}
        {kpiCard("Top Counterparty Share", pct2(summary.top1_share_pct), "Single counterparty exposure")}
      </div>

      {/* Main grid: chart + table */}
      <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 12, marginBottom: 12 }}>
        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, color: "#0B1F33" }}>Top Counterparties — {metricLabel(metric)}</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>
              Tip: switch to <b>Txn Count</b> to detect "many small payments" concentration.
            </div>
          </div>

          <div style={{ maxHeight: 560, overflowY: "auto" }}>
            {loadingReport ? (
              <div style={{ padding: 12, color: "#64748B" }}>Loading…</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(430, chartData.length * 22)}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => (metric === "count" ? int0(v) : money0(v))} style={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={210}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<TooltipBar />} />
                  <Bar dataKey="value" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>Details</div>

          <div style={{ overflowX: "auto", maxHeight: 465, overflowY: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC" }}>
                  <th style={{ padding: 10 }}>Counterparty</th>
                  <th style={{ padding: 10 }}>Country</th>
                  <th style={{ padding: 10 }}>Txns</th>
                  <th style={{ padding: 10 }}>Amount</th>
                  <th style={{ padding: 10 }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const name = normalizeName(r.counterparty_name);
                  const share = Number(r.share_pct || 0);
                  const tone = share >= 12 ? "warn" : share >= 6 ? "info" : "neutral";
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: 10, fontWeight: 700, color: "#0B1F33" }}>{name}</td>
                      <td style={{ padding: 10, color: "#475569" }}>{r.country || "—"}</td>
                      <td style={{ padding: 10 }}>{int0(r.txn_count)}</td>
                      <td style={{ padding: 10 }}>{money0(r.total_amount)}</td>
                      <td style={{ padding: 10 }}><Pill text={pct2(r.share_pct)} tone={tone} /></td>
                    </tr>
                  );
                })}
                {!loadingReport && rows.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 12, color: "#64748B" }}>No data for selected filters.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#64748B" }}>
            Data quality: any <b>UNMAPPED COUNTERPARTY</b> entries indicate missing counterparty enrichment in source feeds.
          </div>
        </div>
      </div>

      {/* Trend — always shows last 12 weeks regardless of selected period type */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: "#0B1F33" }}>Concentration Trend — {metric === "count" ? "Txn Count %" : "Volume $"} (last 12 weeks)</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Tracks <b>Top 1</b> and <b>Top 5</b> share of weekly {metric === "count" ? "transaction count" : "volume"}.</div>
        </div>

        <div style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" style={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)}%`} style={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name === "top5_share_pct" ? "Top 5 share" : "Top 1 share"]} />
              <Line type="monotone" dataKey="top5_share_pct" strokeWidth={3} dot={false} />
              <Line type="monotone" dataKey="top1_share_pct" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </SSBReportShell>
  );
}